import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Monitor, Radio, RotateCcw } from 'lucide-react'
import { AButton, Chip } from './Section'
import { cn } from '../../lib/cn'
import { api, get } from '../../lib/api'
import type { ScreenFocus, ScreenMode } from './screenTypes'

/**
 * What the big screen is showing, and the one control that clears it.
 *
 * **One button, deliberately.** This bar used to offer two — "Stop · show
 * backdrop" and "Follow the event" — and the Committee's verdict after using
 * it was that nobody could tell what either would do or which one they were
 * currently in. The distinction was real but useless: `idle` pins the
 * backdrop and `auto` derives from the data, and *both* are taken off the
 * backdrop by the very next draw or reveal, because the server points the
 * screen at whatever Admin does next (`followStage`).
 *
 * So there is now one action — **Reset screen** — which clears the projector
 * back to the welcome backdrop. Whatever fires next opens by itself. The two
 * modes still exist on the server; this simply stops asking the Committee to
 * choose between them mid-ceremony.
 */

/** What is on the projector right now, in words the Committee uses. */
const LABELS: Record<ScreenMode, string> = {
  auto: 'Following the event',
  idle: 'Welcome backdrop',
  draw: 'Showing a lucky draw',
  award: 'Showing an award',
  cheque: 'Showing the cheque',
}

export function ScreenFocusBar({
  className,
  onError,
}: {
  className?: string
  onError: (message: string | null) => void
}) {
  const queryClient = useQueryClient()

  const focus = useQuery({
    queryKey: ['admin-screen-focus'],
    queryFn: () => get<ScreenFocus>('/api/admin/screen/focus', 'staff'),
    refetchInterval: 5000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
  })

  const reset = useMutation({
    mutationFn: () =>
      api<ScreenFocus>('/api/admin/screen/focus', {
        method: 'POST',
        body: JSON.stringify({ mode: 'idle' }),
        auth: 'staff',
      }),
    onSuccess: () => {
      onError(null)
      queryClient.invalidateQueries({ queryKey: ['admin-screen-focus'] })
    },
    onError: () => onError('Could not reset the screen. Try again.'),
  })

  const mode = focus.data?.mode ?? 'auto'
  const isIdle = mode === 'idle'

  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-2xl border border-white bg-white/95 p-4 shadow-lg shadow-blue-900/5 sm:flex-row sm:items-center sm:justify-between',
        className,
      )}
    >
      <div className="flex min-w-0 items-center gap-3">
        <span
          className={cn(
            'grid size-10 shrink-0 place-items-center rounded-xl',
            isIdle ? 'bg-slate-100 text-slate-500' : 'bg-emerald-100 text-emerald-600',
          )}
        >
          <Monitor className="size-5" strokeWidth={2.2} />
        </span>
        <div className="min-w-0">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-slate-400">
            Stage screen
          </p>
          <p className="flex flex-wrap items-center gap-1.5 text-sm font-bold text-[#0B1E3F]">
            {LABELS[mode]}
            {!isIdle && (
              <Chip tone="positive">
                <Radio className="mr-0.5 size-3 animate-pulse" strokeWidth={2.4} />
                Live
              </Chip>
            )}
          </p>
          <p className="text-xs leading-snug text-slate-500">
            {isIdle
              ? 'The next draw or award opens on the screen by itself.'
              : 'Reset clears it back to the welcome backdrop.'}
          </p>
          {focus.data?.updatedBy && (
            <p className="truncate text-xs text-slate-400">
              Set by {focus.data.updatedBy}
              {focus.data.updatedAt &&
                ` at ${new Date(focus.data.updatedAt).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}`}
            </p>
          )}
        </div>
      </div>

      {/*
        Never refused, and the only control here that is not: it overrides an
        open draw, a finished one, and an announcement still mid-count. A
        panic button that argues back is no use when something is on the
        projector that should not be.
      */}
      <AButton
        tone={isIdle ? 'outline' : 'danger'}
        block
        className="shrink-0 sm:w-auto"
        disabled={reset.isPending || isIdle}
        onClick={() => reset.mutate()}
      >
        <RotateCcw className="size-4 shrink-0" strokeWidth={2.6} />
        {isIdle ? 'Screen is clear' : reset.isPending ? 'Resetting…' : 'Reset screen'}
      </AButton>
    </div>
  )
}
